import { Download } from "lucide-react";
import { cn } from "@lab/src/lib/util";
import { ArchiveFileType } from "@/lib/supabase/supabaseTypes";
import { Checkbox } from "../../../components/ui/checkbox";
import { Button } from "../../../components/ui/button";
import { FileIconComponent } from "../../../components/file-icon/file-icon.component";
import {
	formatArchiveDate,
	getArchiveFileLabel,
	getArchiveIconType,
} from "../archive.utils";
import { ARCHIVE_COLUMNS } from "./archive-file-table-header.component";

interface ArchiveFileRowProps {
	file: ArchiveFileType;
	selected: boolean;
	/** Show the Auftrag the file belongs to (used by the flat/search view). */
	showJobContext?: boolean;
	/** Auftraggeber column, set in the organisation-wide scopes. */
	clientLabel?: string;
	onToggleSelect: (fileId: string) => void;
	onPreview: (file: ArchiveFileType) => void;
	onDownload: (file: ArchiveFileType) => void;
	onOpenJob?: (file: ArchiveFileType) => void;
}

export const ArchiveFileRow: React.FC<ArchiveFileRowProps> = ({
	file,
	selected,
	showJobContext = false,
	clientLabel,
	onToggleSelect,
	onPreview,
	onDownload,
	onOpenJob,
}) => {
	const fileName = file.file_name || "Unbenannte Datei";
	const label = getArchiveFileLabel(file);

	return (
		<div
			className="group flex items-center gap-3 px-3 py-1.5 hover:bg-gray-50"
			data-testid="archive-file-row"
		>
			<Checkbox
				checked={selected}
				onCheckedChange={() => file.id && onToggleSelect(file.id)}
				aria-label={`Datei ${fileName} auswählen`}
				data-testid="archive-file-checkbox"
			/>
			<button
				type="button"
				className="flex min-w-0 flex-1 items-center gap-2 text-left"
				onClick={() => onPreview(file)}
				aria-label={`Datei ${fileName} öffnen`}
				data-testid="archive-file-preview-button"
			>
				<FileIconComponent
					fileType={getArchiveIconType(file)}
					size={16}
				/>
				<span className="min-w-0 truncate" title={fileName}>
					{fileName}
				</span>
			</button>

			{clientLabel !== undefined && (
				<span
					className={cn(
						ARCHIVE_COLUMNS.client,
						"text-sm text-gray-500"
					)}
					title={clientLabel}
				>
					{clientLabel}
				</span>
			)}

			{showJobContext &&
				(file.resolved_job_id && file.resolved_client_id ? (
					<button
						type="button"
						className={cn(
							ARCHIVE_COLUMNS.job,
							"text-left text-sm text-muted-foreground hover:text-foreground hover:underline"
						)}
						onClick={() => onOpenJob?.(file)}
						aria-label={`Zum Auftrag ${
							file.job_code ?? ""
						} wechseln`}
						data-testid="archive-open-job-button"
					>
						{file.job_code || file.job_title || "Auftrag"}
					</button>
				) : (
					<span
						className={cn(
							ARCHIVE_COLUMNS.job,
							"text-sm text-gray-400"
						)}
					>
						Ohne Auftrag
					</span>
				))}

			<span
				className={cn(
					ARCHIVE_COLUMNS.kind,
					"text-sm text-gray-500",
					file.invoice_canceled_at && "text-red-600"
				)}
				title={label}
			>
				{label}
			</span>
			<span className={cn(ARCHIVE_COLUMNS.date, "text-sm text-gray-400")}>
				{formatArchiveDate(file.created_at)}
			</span>
			<Button
				variant="ghost"
				size="icon"
				className="h-7 w-7 shrink-0"
				onClick={() => onDownload(file)}
				aria-label={`Datei ${fileName} herunterladen`}
				data-testid="archive-download-button"
			>
				<Download size={15} />
			</Button>
		</div>
	);
};
